import React from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Link, useDisclosure } from "@heroui/react";
import { Icon } from "@iconify/react";
import { addToast } from "@heroui/react";
import api from "../services/api";

interface PartnerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  company: string;
  phone: string;
  email: string;
}

interface FormErrors {
  name?: string;
  phone?: string;
  email?: string;
}

export const PartnerFormModal: React.FC<PartnerFormModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = React.useState<FormData>({
    name: "",
    company: "",
    phone: "",
    email: ""
  });
  
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  
  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Clear error when user types
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };
  
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = "Пожалуйста, введите ваше имя";
    }
    
    // Validate phone
    if (!formData.phone.trim()) {
      newErrors.phone = "Пожалуйста, введите ваш телефон";
    }
    
    // Validate email with regex
    if (!formData.email.trim()) {
      newErrors.email = "Пожалуйста, введите ваш email";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Пожалуйста, введите корректный email";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Send data to backend endpoint for unique aroma leads
      const payload = {
        name: formData.name.trim(),
        company: formData.company?.trim() || undefined,
        phone: formData.phone.trim(),
        email: formData.email.trim(),
      };

      const resp = await api.post('/leads/unique-aroma/', payload, { headers: { 'Content-Type': 'application/json' } });

      // success — покажем тост, затем закроем модалку
      addToast({
        title: "Заявка отправлена",
        description: "Спасибо! Мы свяжемся с вами в ближайшее время.",
        color: "success",
      });

      // Небольшая задержка, чтобы тост отобразился корректно, затем сброс формы и закрытие модалки
      setTimeout(() => {
        setFormData({ name: "", company: "", phone: "", email: "" });
        try {
          onClose();
        } catch (e) {
          // safety: если onClose неожиданно не функция
          console.warn('onClose not callable', e);
        }
      }, 300);
    } catch (error) {
      console.error("Error submitting unique-aroma lead:", error);
      const msg = error?.response?.data?.message || 'Произошла ошибка при отправке заявки. Пожалуйста, попробуйте позже.';
      addToast({
        title: "Ошибка",
        description: msg,
        color: "danger",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Modal 
      isOpen={isOpen} 
      onOpenChange={onClose}
      backdrop="blur"
      placement="center"
      size="md"
      classNames={{
        base: "border border-divider bg-content1"
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h3 className="text-xl font-medium">Оставить заявку</h3>
            </ModalHeader>
            <ModalBody>
              <div className="space-y-4">
                <Input
                  label="Имя"
                  placeholder="Введите ваше имя"
                  value={formData.name}
                  onValueChange={(value) => handleChange("name", value)}
                  variant="bordered"
                  isRequired
                  color={errors.name ? "danger" : "default"}
                  errorMessage={errors.name}
                />
                
                <Input
                  label="Компания"
                  placeholder="Введите название компании"
                  value={formData.company}
                  onValueChange={(value) => handleChange("company", value)}
                  variant="bordered"
                />
                
                <Input
                  label="Телефон"
                  placeholder="+7 (___) ___-__-__"
                  type="tel"
                  value={formData.phone}
                  onValueChange={(value) => handleChange("phone", value)}
                  variant="bordered"
                  isRequired
                  color={errors.phone ? "danger" : "default"}
                  errorMessage={errors.phone}
                />
                
                <Input
                  label="Email"
                  placeholder="example@email.com"
                  type="email"
                  value={formData.email}
                  onValueChange={(value) => handleChange("email", value)}
                  variant="bordered"
                  isRequired
                  color={errors.email ? "danger" : "default"}
                  errorMessage={errors.email}
                />
              </div>
              
              <p className="text-xs text-foreground/60 mt-4">
                Отправляя форму, вы соглашаетесь с нашей{" "}
                <Link href="/privacy" underline="always" size="sm" className="text-xs">
                  Политикой конфиденциальности
                </Link>
              </p>
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onPress={onClose}>
                Отмена
              </Button>
              <Button 
                color="primary" 
                onPress={handleSubmit}
                isLoading={isSubmitting}
                endContent={!isSubmitting && <Icon icon="lucide:send" width={16} />}
              >
                Отправить
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};